import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery
} from "@mui/material";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { ChangeMachine, CatalogModelListItem, CustomerMachineryState, Machine, MachineFileItem } from "../models/models";
import { integerRegex } from "../../../shared/validators";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { MAX_FILE_SIZE_MB } from "../../../shared/models/models";
import { MultiImageUpload } from "../../../shared/components/gallery";
import type { GalleryImage } from "../../../shared/components/gallery";
import { changeMachine, createMachine, getAxisConfigurations, getMachines, getManufacturers, getOperationTypes, getControlSystemBrands, getControlSystemModels, getAutomationLevels } from "../customerMachinerySlice";
import { fetchLocations } from "../customerLocationsSlice";
import ProductionLineForm from "./machineTypeForms/ProductionLineForm";
import { MachineTypes } from "../models/models";
import CncForm from "./machineTypeForms/CncForm";
import CatalogModelSearch from "./CatalogModelSearch";
import { dt, accentShimmer } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { formatManufacturerWithHierarchy } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";
import LocationPicker from "./locations/LocationPicker";

export interface MachineDialogProps {
  open: boolean;
  onClose: () => void;
  machine?: Machine;
  isNew?: boolean;
  preSelectedCatalogModel?: CatalogModelListItem;
  onCreated?: (newMachineId?: string) => void;
}

type DialogMode = "hub" | "catalog-search" | "custom-form";

/* ------------------------------------------------------------------ */
/*  Shared input styling — dt-token-based form fields                  */
/* ------------------------------------------------------------------ */
const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: dt.font,
    fontSize: "14px",
    borderRadius: dt.radiusSm,
    backgroundColor: dt.inputBg,
    "& fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: dt.cardBorder },
    "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input::placeholder": { opacity: 0.6 },
};

const selectSx = {
  fontFamily: dt.font,
  fontSize: "14px",
  backgroundColor: dt.inputBg,
  borderRadius: dt.radiusSm,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: dt.cardBorder },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: dt.blue, borderWidth: "1.5px" },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 250,
      borderRadius: dt.radiusSm,
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    },
  },
};

const sectionLabelSx = {
  fontSize: "12px",
  fontWeight: 700,
  fontFamily: dt.font,
  textTransform: "uppercase" as const,
  letterSpacing: "1px",
  color: dt.textPrimary,
  mb: 0,
};

/* ------------------------------------------------------------------ */
/*  Section label renderer (icon + uppercase label)                    */
/* ------------------------------------------------------------------ */
const SectionLabel: React.FC<{ icon: React.ReactNode; label: string; required?: boolean }> = ({ icon, label, required }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
    {icon}
    <Box sx={sectionLabelSx}>
      {label}
      {required && <span style={{ color: "#C0392B", marginLeft: 4 }}>*</span>}
    </Box>
  </Box>
);

const MachineDialog = ({ machine, open, onClose, isNew, preSelectedCatalogModel, onCreated }: MachineDialogProps) => {
  useRedesignSetup();

  const { query, manufacturers, types, operationTypes, axisConfigurations, controlSystemBrands, controlSystemModels, automationLevels } = useAppSelector<CustomerMachineryState>(
    (state: any) => state.customerMachinery
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const isNarrow = useMediaQuery("(max-width:500px)");

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    watch,
    setValue,
    clearErrors,
    control,
    formState: { errors }
  } = useForm<ChangeMachine>();

  // Dialog mode: hub (search + 2 cards), catalog-search, or custom-form
  const [dialogMode, setDialogMode] = useState<DialogMode>(
    isNew ? (preSelectedCatalogModel ? "catalog-search" : "hub") : "custom-form"
  );

  // Catalog search state
  const [selectedCatalogModel, setSelectedCatalogModel] = useState<CatalogModelListItem | null>(null);
  const [catalogModelId, setCatalogModelId] = useState<string | undefined>(undefined);
  const [manualMode, setManualMode] = useState(false);

  // Location state — wired to Redux
  const [locationId, setLocationId] = useState<string | null>(null);
  const locations = useAppSelector((state) => state.customerLocations.locations);

  // Fetch locations if not yet loaded
  useEffect(() => {
    if (locations.length === 0) {
      dispatch(fetchLocations());
    }
  }, [dispatch, locations.length]);

  const onSubmit: SubmitHandler<ChangeMachine> = (data: ChangeMachine) => {
    // Attach catalogModelId and locationId to the submitted data
    data.catalogModelId = catalogModelId;
    data.locationId = locationId || undefined;
    if (isNew) {
      handleCreateMachine(data);
    } else {
      handleChangeMachine(data);
    }
  };

  const typeId = watch("typeId");
  const manufacturerId = watch("manufacturerId");
  const operationTypeId = watch("operationTypeId");
  const controlSystemBrandId = watch("controlSystemBrandId");

  // Gallery images state: load from machine.files, or fall back to legacy single image
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>(() => {
    if (machine?.files && machine.files.length > 0) {
      return [...machine.files]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((f, idx) => ({
          id: f.id || `existing-${idx}`,
          src: f.content,
          name: f.name,
          size: f.size,
          isPrimary: f.isPrimary,
        }));
    }
    if (machine?.image?.content) {
      return [{
        id: "existing-image",
        src: machine.image.content,
        name: machine.image.name || "machine-image",
        isPrimary: true,
      }];
    }
    return [];
  });

  // Convert GalleryImage[] to MachineFileItem[] for API submission
  const getFilesFromGallery = (): MachineFileItem[] | undefined => {
    if (galleryImages.length === 0) return undefined;
    return galleryImages.map((img, idx) => ({
      id: img.isNew ? undefined : img.id,
      name: img.name || `image-${idx + 1}`,
      content: img.src,
      displayOrder: idx,
      isPrimary: img.isPrimary === true,
      size: img.size,
    }));
  };

  const handleCatalogModelSelect = (model: CatalogModelListItem) => {
    setSelectedCatalogModel(model);
    setCatalogModelId(model.id);
    setManualMode(false);

    // Find CNC type (all Techspex models are CNC machines)
    const cncType = types.find((t) => t.name === MachineTypes.cnc);

    // Reset form with catalog values — hides the old dropdown form
    // Use undefined (not "") for IDs that may be null — empty string causes
    // 400 on the backend because it can't deserialize "" as a Guid.
    // Phase 7E: Don't send manufacturerId from frontend — backend resolves it
    // from CatalogManufacturerId via ResolveCatalogFieldsAsync. The old
    // manufacturer?.manufacturerId pointed to the legacy Manufacturers table
    // which causes 400 errors since Machine.ManufacturerId now references CatalogManufacturers.
    reset({
      model: model.modelName,
      typeId: cncType?.id || undefined,
      manufacturerId: undefined,
      operationTypeId: undefined,
      axisConfigurationId: undefined,
      controlSystemBrandId: undefined,
      automationLevelId: undefined,
      serialNumber: "",
      customBrandName: "",
      numberOfStations: undefined,
      system: undefined,
      version: undefined,
      productionYear: undefined,
      description: undefined,
      catalogModelId: model.id,
      image: null,
    });
    clearErrors();
  };

  const handleClearCatalogModel = () => {
    setSelectedCatalogModel(null);
    setCatalogModelId(undefined);
  };

  const handleSwitchToManual = () => {
    setManualMode(true);
    setSelectedCatalogModel(null);
    setCatalogModelId(undefined);
  };

  const handleSwitchToCatalog = () => {
    setManualMode(false);
  };

  // Navigate back to hub and reset form state
  const handleBackToHub = () => {
    setDialogMode("hub");
    setSelectedCatalogModel(null);
    setCatalogModelId(undefined);
    setManualMode(false);
    setLocationId(null);
    setGalleryImages([]);
    reset({
      model: "",
      serialNumber: "",
      typeId: "",
      manufacturerId: "",
      customBrandName: "",
      operationTypeId: "",
      axisConfigurationId: undefined,
      controlSystemBrandId: "",
      automationLevelId: undefined,
      numberOfStations: undefined,
      system: undefined,
      version: undefined,
      productionYear: undefined,
      description: undefined,
      catalogModelId: undefined,
      image: null
    });
    clearErrors();
  };

  const handleChangeMachine = async (model: ChangeMachine) => {
    if (machine) {
      // For catalog machines: send form values (user may have changed them),
      // fall back to existing machine values, then let backend re-resolve any nulls
      if (machine.catalogModelId) {
        model.catalogModelId = machine.catalogModelId;
        model.typeId = model.typeId || machine.type?.id;
        const manufacturerId = manufacturers.find((x) => x.id === model.manufacturerId)?.id;
        model.manufacturerId = manufacturerId || machine?.manufacturer?.id;
        const operationTypeId = operationTypes.find((x) => x.id === model.operationTypeId)?.id;
        model.operationTypeId = operationTypeId || machine.operationType?.id;
        const axisConfigurationId = axisConfigurations.find((x) => x.name === model.axisConfigurationId)?.id;
        model.axisConfigurationId = axisConfigurationId || machine.axisConfiguration?.id;
        const controlSystemBrandId = controlSystemBrands.find((x) => x.id === model.controlSystemBrandId)?.id;
        model.controlSystemBrandId = controlSystemBrandId || machine.controlSystemBrand?.id;
        const automationLevelId = automationLevels.find((x) => x.id === model.automationLevelId)?.id;
        model.automationLevelId = automationLevelId || machine.automationLevel?.id;
      } else {
        const manufacturerId = manufacturers.find((x) => x.id === model.manufacturerId)?.id;
        const operationTypeId = operationTypes.find((x) => x.id === model.operationTypeId)?.id;
        const axisConfigurationId = axisConfigurations.find((x) => x.name === model.axisConfigurationId)?.id;
        const controlSystemBrandId = controlSystemBrands.find((x) => x.id === model.controlSystemBrandId)?.id;
        model.manufacturerId = manufacturerId ? manufacturerId : machine?.manufacturer?.id;
        model.operationTypeId = operationTypeId ? operationTypeId : machine.operationType?.id;
        model.axisConfigurationId = axisConfigurationId ? axisConfigurationId : machine.axisConfiguration?.id;
        model.controlSystemBrandId = controlSystemBrandId ? controlSystemBrandId : machine.controlSystemBrand?.id;
      }
      model.description = model.description ? model.description : undefined;
      model.productionYear = model.productionYear ? model.productionYear : undefined;
      model.system = model.system ? model.system : undefined;
      model.version = model.version ? model.version : undefined;
      model.image = null; // Legacy field — using files[] instead
      model.files = getFilesFromGallery();
      onClose();
      await dispatch(changeMachine({ id: machine.id, model }));
      reset();
      await dispatch(getMachines(query));
    }
  };

  const handleCreateMachine = async (model: ChangeMachine) => {
    // If catalog model is selected, ensure required backend fields are set from catalog data
    if (catalogModelId && selectedCatalogModel) {
      model.typeId = undefined; // MACH-TYPE-DEFAULT-1: backend resolves it from CatalogMachineModel.MachineTypeId (CNC fallback)
      // Phase 7E: Don't send manufacturerId — backend resolves it from
      // CatalogManufacturerId via ResolveCatalogFieldsAsync. The old
      // manufacturer?.manufacturerId pointed to the legacy Manufacturers table.
      model.manufacturerId = undefined;
      // Catalog machines don't need these — backend accepts null
      model.operationTypeId = undefined;
      model.controlSystemBrandId = undefined;
      model.axisConfigurationId = undefined;
      model.automationLevelId = undefined;
    } else {
      const operationTypeId = operationTypes.find((x) => x.id === model.operationTypeId)?.id;
      const axisConfigurationId = axisConfigurations.find((x) => x.name === model.axisConfigurationId)?.id;
      const controlSystemBrandId = controlSystemBrands.find((x) => x.id === model.controlSystemBrandId)?.id;
      const automationLevelId = automationLevels.find((x) => x.id === model.automationLevelId)?.id;
      model.operationTypeId = operationTypeId || undefined;
      model.axisConfigurationId = axisConfigurationId;
      model.controlSystemBrandId = controlSystemBrandId || undefined;
      model.automationLevelId = automationLevelId;

      // For non-catalog machines, verify manufacturer is in the loaded list
      const manufacturerId = manufacturers.find((x) => x.id === model.manufacturerId)?.id;
      model.manufacturerId = manufacturerId || model.manufacturerId || undefined;
    }

    model.description = model.description ? model.description : undefined;
    model.productionYear = model.productionYear ? model.productionYear : undefined;
    model.system = model.system ? model.system : undefined;
    model.version = model.version ? model.version : undefined;
    model.image = null; // Legacy field — using files[] instead
    model.files = getFilesFromGallery();
    onClose();
    const result = await dispatch(createMachine(model)).unwrap();
    onCreated?.(result);
    reset();
    await dispatch(getMachines(query));
  };

  useEffect(() => {
    if (machine) {
      const changeMachine: ChangeMachine = {
        model: machine.model || "",
        serialNumber: machine.serialNumber || "",
        typeId: machine.type?.id || "",
        manufacturerId: machine.manufacturer?.id || "",
        customBrandName: machine.customBrandName || "",
        operationTypeId: machine.operationType?.id || "",
        axisConfigurationId: machine.axisConfiguration?.id,
        controlSystemBrandId: machine.controlSystemBrand?.id || "",
        automationLevelId: machine.automationLevel?.id,
        numberOfStations: machine.numberOfStations,
        system: machine.system,
        version: machine.version,
        productionYear: machine.productionYear,
        description: machine.description,
        catalogModelId: machine.catalogModelId,
        image: machine.image
      };
      reset(changeMachine);

      // Restore catalog model selection for editing
      if (machine.catalogModel) {
        setSelectedCatalogModel(machine.catalogModel);
        setCatalogModelId(machine.catalogModelId);
        setManualMode(false);
      } else {
        setSelectedCatalogModel(null);
        setCatalogModelId(machine.catalogModelId);
        setManualMode(true);
      }
      // Sync gallery images for edit mode — prefer files[], fall back to legacy image
      if (machine.files && machine.files.length > 0) {
        setGalleryImages(
          [...machine.files]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((f, idx) => ({
              id: f.id || `existing-${idx}`,
              src: f.content,
              name: f.name,
              size: f.size,
              isPrimary: f.isPrimary,
            }))
        );
      } else if (machine.image?.content) {
        setGalleryImages([{
          id: "existing-image",
          src: machine.image.content,
          name: machine.image.name || "machine-image",
          isPrimary: true,
        }]);
      } else {
        setGalleryImages([]);
      }
      // Edit mode always goes to custom-form
      setDialogMode("custom-form");
    } else {
      const createMachine: ChangeMachine = {
        model: "",
        serialNumber: "",
        typeId: "",
        manufacturerId: "",
        customBrandName: "",
        operationTypeId: "",
        axisConfigurationId: undefined,
        controlSystemBrandId: "",
        automationLevelId: undefined,
        numberOfStations: undefined,
        system: undefined,
        version: undefined,
        productionYear: undefined,
        description: undefined,
        catalogModelId: undefined,
        image: null
      };
      reset(createMachine);
      setSelectedCatalogModel(null);
      setCatalogModelId(undefined);
      setManualMode(false);
      setGalleryImages([]);
      // New machine starts at hub (unless pre-selected model is provided)
      if (isNew && !preSelectedCatalogModel) {
        setDialogMode("hub");
      }
    }
  }, [machine, reset]);

  useEffect(() => {
    if (typeId) {
      dispatch(getManufacturers(typeId));
      dispatch(getOperationTypes(typeId));
      dispatch(getControlSystemBrands(typeId));
      dispatch(getAutomationLevels());
    }
  }, [typeId])

  useEffect(() => {
    if (operationTypeId) {
      dispatch(getAxisConfigurations(operationTypeId));
    }
  }, [operationTypeId])

  useEffect(() => {
    if (controlSystemBrandId) {
      dispatch(getControlSystemModels(controlSystemBrandId));
    }
  }, [controlSystemBrandId])

  // Pre-select catalog model when provided (e.g. from catalog browse/detail page)
  useEffect(() => {
    if (preSelectedCatalogModel && isNew && open) {
      handleCatalogModelSelect(preSelectedCatalogModel);
      setDialogMode("catalog-search");
    }
  }, [preSelectedCatalogModel, isNew, open]);

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "machine-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes machineDialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Determine dialog width based on mode
  const dialogMaxWidth = dialogMode === "hub" ? "sm" : "xs";

  // For catalog-search mode: show reduced fields when catalog model is selected
  const showReducedCatalogFields = dialogMode === "catalog-search" && !!selectedCatalogModel;

  // Hub card definitions (search is always-visible above cards)
  const hubCards = [
    {
      key: "browse",
      icon: <ViewListOutlinedIcon sx={{ fontSize: 32, color: "#27ae60" }} />,
      title: translate("browseCatalog"),
      description: translate("browseCatalogDesc"),
      iconBg: "rgba(39, 174, 96, 0.08)",
      hoverBorder: "#27ae60",
      onClick: () => {
        onClose();
        navigate("/machinery/catalog");
      },
    },
    {
      key: "custom",
      icon: <BuildOutlinedIcon sx={{ fontSize: 32, color: "#8e44ad" }} />,
      title: translate("addCustomMachine"),
      description: translate("addCustomMachineDesc"),
      iconBg: "rgba(142, 68, 173, 0.08)",
      hoverBorder: "#8e44ad",
      onClick: () => {
        setManualMode(true);
        setDialogMode("custom-form");
      },
    },
  ];

  // Handle catalog model selection from hub search bar
  const handleHubCatalogModelSelect = (model: CatalogModelListItem) => {
    handleCatalogModelSelect(model);
    setDialogMode("catalog-search");
  };

  // Determine dialog title
  const getDialogTitle = () => {
    if (!isNew) return translate("editMachine");
    if (dialogMode === "hub") return translate("addMachine");
    if (dialogMode === "catalog-search") return translate("searchCatalog");
    return translate("addCustomMachine");
  };

  // Dialog icon based on mode
  const getDialogIcon = () => {
    if (!isNew) {
      return { icon: <BuildOutlinedIcon sx={{ fontSize: 22, color: dt.blue }} />, bg: dt.blueSoft };
    }
    if (dialogMode === "hub") {
      return { icon: <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 22, color: dt.blue }} />, bg: dt.blueSoft };
    }
    if (dialogMode === "catalog-search") {
      return { icon: <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 22, color: "#27AE60" }} />, bg: "rgba(39,174,96,0.08)" };
    }
    return { icon: <BuildOutlinedIcon sx={{ fontSize: 22, color: "#8E44AD" }} />, bg: "rgba(142,68,173,0.08)" };
  };

  const dialogIcon = getDialogIcon();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={dialogMaxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "machineDialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          transition: "max-width 0.3s ease",
        },
      }}
    >
      {/* BLUE GRADIENT ACCENT BAR */}
      <Box
        sx={{
          height: "4px",
          background: accentShimmer,
          flexShrink: 0,
        }}
      />

      {/* HEADER — centered icon + title */}
      <DialogTitle sx={{ textAlign: "center", pt: 2.5, pb: 0.5, px: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: dialogIcon.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {dialogIcon.icon}
          </Box>
          <Box
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.3px",
            }}
          >
            {getDialogTitle()}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 1,
          pb: 2,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.12)", borderRadius: "2px" },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.12) transparent",
        }}
      >
        {/* ===== HUB VIEW: search bar + "or" divider + 2-card selection ===== */}
        {dialogMode === "hub" && isNew && (
          <Box sx={{ py: 1 }}>
            {/* Always-visible search bar */}
            <CatalogModelSearch
              onSelect={handleHubCatalogModelSelect}
              selectedModel={null}
              onClear={undefined}
            />

            {/* "or" divider */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2.5 }}>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: dt.cardBorder }} />
              <Box sx={{
                fontSize: "11px",
                color: dt.textMuted,
                fontFamily: dt.font,
                textTransform: "uppercase",
                letterSpacing: "1.2px",
                fontWeight: 600,
              }}>
                {tCommon("or")}
              </Box>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: dt.cardBorder }} />
            </Box>

            {/* 2-card grid: Browse Full Catalog + Add Custom Machine */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isNarrow ? "1fr" : "repeat(2, 1fr)",
                gap: 2,
              }}
            >
              {hubCards.map((card) => (
                <Box
                  key={card.key}
                  onClick={card.onClick}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    padding: isNarrow ? "16px 20px" : "24px 16px",
                    background: dt.cardBg,
                    border: `1.5px solid ${dt.cardBorder}`,
                    borderRadius: dt.radius,
                    cursor: "pointer",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: card.hoverBorder,
                      boxShadow: `0 0 0 3px ${card.hoverBorder}20, 0 4px 16px rgba(0,0,0,0.06)`,
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      backgroundColor: card.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      fontFamily: dt.font,
                      color: dt.textPrimary,
                      mb: 0.5,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.title}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "12px",
                      fontFamily: dt.font,
                      color: dt.textSecondary,
                      lineHeight: 1.5,
                    }}
                  >
                    {card.description}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ===== CATALOG SEARCH VIEW ===== */}
        {dialogMode === "catalog-search" && isNew && (
          <Box sx={{ animation: "fadeIn 0.2s ease" }}>
            {/* Back to hub link */}
            <Box
              onClick={handleBackToHub}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                mb: 2,
                cursor: "pointer",
                fontSize: "13px",
                color: dt.blue,
                fontFamily: dt.font,
                fontWeight: 500,
                transition: "all 0.15s ease",
                "&:hover": {
                  color: dt.blueHover,
                  textDecoration: "underline",
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
              {translate("backToOptions")}
            </Box>

            {/* Catalog search component */}
            <Box sx={{ mb: 2 }}>
              <SectionLabel
                icon={<PrecisionManufacturingOutlinedIcon sx={{ fontSize: 16, color: dt.textSecondary }} />}
                label={translate("selectFromCatalog")}
              />
              <CatalogModelSearch
                onSelect={handleCatalogModelSelect}
                selectedModel={selectedCatalogModel}
                onClear={handleClearCatalogModel}
              />
            </Box>

            {/* Reduced fields after catalog model selection */}
            {showReducedCatalogFields && (
              <form id="form" onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
                  <Box>
                    <SectionLabel
                      icon={<Box sx={{ width: 16 }} />}
                      label={translate("serialNumber")}
                      required
                    />
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={translate("serialNumberPlaceholder")}
                      {...register("serialNumber", { required: tCommon("forms.required") })}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.serialNumber}
                      helperText={errors.serialNumber?.message}
                      sx={inputSx}
                    />
                  </Box>
                  <Box>
                    <SectionLabel
                      icon={<Box sx={{ width: 16 }} />}
                      label={translate("productionYear")}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={moment().year().toString()}
                      {...register("productionYear", {
                        pattern: { value: integerRegex, message: translate("fieldRequire4Digits") },
                        min: { value: 1900, message: translate("enterValidYearRange", { year: moment().year() }) },
                        max: { value: moment().year(), message: translate("enterValidYearRange", { year: moment().year() }) }
                      })}
                      inputProps={{ maxLength: 4 }}
                      error={!!errors.productionYear}
                      helperText={errors.productionYear?.message}
                      sx={inputSx}
                    />
                  </Box>
                </Box>

                {/* Location picker */}
                <Box sx={{ mt: 2 }}>
                  <SectionLabel
                    icon={<PlaceOutlinedIcon sx={{ fontSize: 16, color: dt.textSecondary }} />}
                    label={translate("location") || "Location"}
                  />
                  <LocationPicker
                    value={locationId}
                    onChange={setLocationId}
                    locations={locations}
                  />
                </Box>
              </form>
            )}
          </Box>
        )}

        {/* ===== CUSTOM FORM VIEW (also used for edit mode) ===== */}
        {dialogMode === "custom-form" && (
          <Box sx={{ animation: isNew ? "fadeIn 0.2s ease" : undefined }}>
            {/* Back to hub link (only for new machines) */}
            {isNew && (
              <Box
                onClick={handleBackToHub}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 2,
                  cursor: "pointer",
                  fontSize: "13px",
                  color: dt.blue,
                  fontFamily: dt.font,
                  fontWeight: 500,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    color: dt.blueHover,
                    textDecoration: "underline",
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 14 }} />
                {translate("backToOptions")}
              </Box>
            )}

            {/* Catalog model indicator for edit mode */}
            {!isNew && machine?.catalogModel && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  background: dt.blueSoft,
                  border: `1px solid ${dt.blueGlow}`,
                  borderRadius: dt.radiusSm,
                  padding: "10px 14px",
                }}
              >
                <PrecisionManufacturingOutlinedIcon
                  sx={{ color: dt.blue, fontSize: 16 }}
                />
                <Box sx={{ fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("catalogModel")}: <strong style={{ color: dt.textPrimary }}>
                    {machine.catalogModel.manufacturer
                      ? formatManufacturerWithHierarchy(
                          machine.catalogModel.manufacturer.name,
                          machine.catalogModel.manufacturer.parentManufacturerName
                        ) + " "
                      : ""}
                    {machine.catalogModel.modelName}
                  </strong>
                </Box>
              </Box>
            )}

            <form id="form" onSubmit={handleSubmit(onSubmit)}>
              {/* Image upload section */}
              <Box sx={{ mb: 2.5 }}>
                <SectionLabel
                  icon={<ImageOutlinedIcon sx={{ fontSize: 16, color: dt.textSecondary }} />}
                  label={translate("productImage")}
                />
                <Box
                  sx={{
                    border: `1.5px dashed ${dt.cardBorder}`,
                    borderRadius: dt.radiusSm,
                    overflow: "hidden",
                    transition: "border-color 0.2s ease",
                    "&:hover": { borderColor: dt.blue },
                  }}
                >
                  <MultiImageUpload
                    images={galleryImages}
                    onChange={setGalleryImages}
                    maxImages={10}
                    maxFileSizeMb={MAX_FILE_SIZE_MB}
                    compact
                  />
                </Box>
              </Box>

              {/* Machine Type selector */}
              <Box sx={{ mb: 2.5 }}>
                <SectionLabel
                  icon={<CategoryOutlinedIcon sx={{ fontSize: 16, color: dt.textSecondary }} />}
                  label={translate("machineType")}
                  required
                />
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    disabled={!isNew}
                    defaultValue={machine?.type?.id || ""}
                    onClose={() => setTimeout(() => trigger("typeId"))}
                    {...register("typeId", { required: tCommon("forms.required") })}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box sx={{ color: dt.textMuted }}>{translate("chooseFromList")}</Box>;
                      }
                      return types.find((x) => x.id === selected)?.name;
                    }}
                    error={!!errors.typeId}
                    sx={selectSx}
                    MenuProps={selectMenuProps}
                  >
                    {types?.map((type) => (
                      <MenuItem key={type.id} value={type.id} sx={{ fontFamily: dt.font, fontSize: "14px" }}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>{errors.typeId?.message}</FormHelperText>
                </FormControl>
              </Box>

              {/* Location picker — between Machine Type and type-specific sub-forms */}
              <Box sx={{ mb: 2.5 }}>
                <SectionLabel
                  icon={<PlaceOutlinedIcon sx={{ fontSize: 16, color: dt.textSecondary }} />}
                  label={translate("location") || "Location"}
                />
                <LocationPicker
                  value={locationId}
                  onChange={setLocationId}
                  locations={locations}
                />
              </Box>

              {typeId && types.find((x) => x.id === typeId)?.name === MachineTypes.cnc && (
                <CncForm
                  machine={machine}
                  control={control}
                  register={register}
                  errors={errors}
                  watch={watch}
                  trigger={trigger}
                  clearErrors={clearErrors}
                />
              )}

              {typeId && types.find((x) => x.id === typeId)?.name === MachineTypes.productionLine && (
                <ProductionLineForm
                  machine={machine}
                  control={control}
                  register={register}
                  errors={errors}
                  watch={watch}
                  trigger={trigger}
                  clearErrors={clearErrors}
                />
              )}
            </form>
          </Box>
        )}
      </DialogContent>

      {/* FOOTER — styled action buttons */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          justifyContent: "center",
          gap: 1.5,
          borderTop: `1px solid ${dt.rowBorder}`,
        }}
      >
        {dialogMode === "hub" ? (
          /* Hub mode: only Cancel */
          <Button
            onClick={onClose}
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              color: dt.blue,
              padding: "8px 24px",
              borderRadius: dt.radiusSm,
              "&:hover": { backgroundColor: dt.blueSoft },
            }}
          >
            {tCommon("buttons.cancel")}
          </Button>
        ) : (
          /* Form modes: Cancel + Submit */
          <>
            <Button
              onClick={onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
                color: dt.textSecondary,
                padding: "8px 24px",
                borderRadius: dt.radiusSm,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: dt.inputBg,
                  color: dt.textPrimary,
                },
              }}
            >
              {tCommon("buttons.cancel")}
            </Button>

            <Button
              form="form"
              type="submit"
              variant="contained"
              startIcon={
                isNew
                  ? <AddCircleOutlineIcon sx={{ fontSize: "18px !important" }} />
                  : <SaveOutlinedIcon sx={{ fontSize: "18px !important" }} />
              }
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: dt.blue,
                padding: "10px 28px",
                borderRadius: dt.radiusSm,
                boxShadow: "none",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: dt.blueHover,
                  boxShadow: "0 2px 8px rgba(52,152,219,0.3)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              {isNew ? translate("addMachine") : tCommon("buttons.save")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MachineDialog;
